import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../../../shared/ui-components/button/button.component';
import { SvgIconComponent } from 'angular-svg-icon';

/**
 * Notification Item Type
 */
export type NotificationType = 'success' | 'info' | 'warning' | 'danger';

/**
 * Notification Item Data Interface
 */
export interface NotificationItemData {
    id?: string | number;
    title: string;
    message: string;
    type: NotificationType;
    actionButton?: {
        label: string;
        onClick?: () => void;
    };
}

/**
 * Reusable Notification Item Component
 * 
 * Displays a single notification item with icon, title, description, optional action button, and dismiss button.
 * Supports four types: success, info, warning, and danger.
 * 
 * @example
 * ```html
 * <app-notification-item
 *   [title]="'Notification title'"
 *   [message]="'Notification message'"
 *   [type]="'info'"
 *   [actionButton]="{ label: 'Click to action', onClick: handleAction }"
 *   (onDismiss)="handleDismiss()"
 *   (onClick)="handleClick()"
 * />
 * ```
 */
@Component({
    selector: 'app-notification-item',
    standalone: true,
    imports: [CommonModule, ButtonComponent, SvgIconComponent],
    templateUrl: './notification-item.component.html',
    styles: [`
    :host {
      display: block;
    }

    .notification-item-non-clickable {
      cursor: default;
    }

    .notification-item-non-clickable:hover {
      background: var(--color-whiteBackground) !important;
    }

    /* Style SVG icons based on notification type */
    .notification-icon-info svg-icon {
      color: var(--color-info);
    }

    .notification-icon-success svg-icon {
      color: var(--color-success);
    }

    .notification-icon-warning svg-icon {
      color: var(--color-warning);
    }

    .notification-icon-error svg-icon {
      color: var(--color-error);
    }

    .notification-item-text ul {
      list-style: none;
      padding-left: 0;
      margin-top: 0.5rem;
    }

    .notification-item-text li {
      position: relative;
      padding-left: 1.25rem;
      margin-bottom: 0.25rem;
    }

    .notification-item-text li::before {
      content: '•';
      position: absolute;
      left: 0;
    }
  `]
})
export class NotificationItemComponent {
    /**
     * Notification title
     */
    @Input() title: string = '';

    /**
     * Notification message/description
     */
    @Input() message: string = '';

    /**
     * Notification type
     * Options: 'success' | 'info' | 'warning' | 'danger'
     */
    @Input() type: NotificationType = 'info';

    /**
     * Optional action button configuration
     */
    @Input() actionButton?: {
        label: string;
        onClick?: () => void;
    };

    /**
     * Whether the notification is clickable
     * Default: true
     */
    @Input() clickable: boolean = true;

    /**
     * Whether to show the dismiss button
     * Default: true
     */
    @Input() showDismiss: boolean = true;

    /**
     * Event emitted when notification item is clicked
     */
    @Output() onClick = new EventEmitter<void>();

    /**
     * Event emitted when dismiss button is clicked
     */
    @Output() onDismiss = new EventEmitter<void>();

    /**
     * Event emitted when action button is clicked
     */
    @Output() onActionClick = new EventEmitter<void>();

    /**
     * Get the SVG icon path based on notification type
     */
    getIconPath(): string {
        switch (this.type) {
            case 'success':
                return '/icons/check-circle.svg';
            case 'info':
                return '/icons/alert.svg';
            case 'warning':
                return '/icons/alert.svg';
            case 'danger':
                return '/icons/alert.svg';
            default:
                return '/icons/alert.svg';
        }
    }
    /**
     * Get the complete SVG icon classes including size and color
     */
    getIconClasses(): string {
        const baseClasses = 'w-5 h-5';
        const colorClass = this.getIconColor();
        return `${baseClasses} ${colorClass}`;
    }

    /**
     * Get the color class based on notification type
     */
    getIconColor(): string {
        switch (this.type) {
            case 'success':
                return 'text-notificationIconSuccess';
            case 'info':
                return 'text-notificationIconInfo';
            case 'warning':
                return 'text-notificationIconWarning';
            case 'danger':
                return 'text-notificationIconError';
            default:
                return 'text-notificationIconInfo';
        }
    }

    /**
     * Get the icon wrapper class based on notification type
     */
    getIconWrapperClass(): string {
        return `notification-icon-wrapper notification-icon-${this.type}`;
    }

    /**
     * Handle notification item click
     */
    handleClick(event: Event): void {
        if (!this.clickable) {
            return;
        }
        this.onClick.emit();
    }

    /**
     * Handle dismiss button click
     */
    handleDismiss(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.onDismiss.emit();
    }

    /**
     * Handle action button click
     */
    handleActionClick(event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        if (this.actionButton?.onClick) {
            this.actionButton.onClick();
        }

        this.onActionClick.emit();
    }
}
